import { Link } from "react-router-dom";
import { Compass, MapPin, BookOpen, Camera, Users, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/lib/lang";

const BADGES = [
  { emoji: "🏕️", frName: "Explorateur débutant", enName: "Beginner Explorer", frDesc: "Première découverte validée", enDesc: "First validated discovery" },
  { emoji: "🗺️", frName: "Cartographe des mystères", enName: "Mystery Cartographer", frDesc: "10 lieux mystérieux visités", enDesc: "10 mystery places visited" },
  { emoji: "🏛️", frName: "Chercheur d'histoire", enName: "History Scholar", frDesc: "Lieux historiques documentés", enDesc: "Documented historical places" },
  { emoji: "👻", frName: "Chasseur de fantômes", enName: "Ghost Hunter", frDesc: "Plusieurs lieux hantés explorés", enDesc: "Multiple haunted places explored" },
  { emoji: "🛸", frName: "Enquêteur du paranormal", enName: "Paranormal Investigator", frDesc: "Lieux liés aux phénomènes inexpliqués", enDesc: "Sites linked to unexplained phenomena" },
  { emoji: "🎖️", frName: "Explorateur militaire", enName: "Military Explorer", frDesc: "Sites d'histoire militaire visités", enDesc: "Military history sites visited" },
  { emoji: "🌎", frName: "Grand explorateur", enName: "Great Explorer", frDesc: "Lieux dans plusieurs pays", enDesc: "Places across multiple countries" },
];

export default function AdventurerConcept() {
  const { lang } = useLang();
  const T = lang === "fr" ? FR : EN;

  return (
    <section className="relative border-t border-zinc-200 bg-white px-6 md:px-12 py-20" data-testid="adventurer-concept">
      <div className="max-w-6xl mx-auto">
        <div className="mb-4 flex items-center gap-3 text-[#8b6f3d]">
          <div className="h-px w-12 bg-[#8b6f3d]/40" />
          <span className="font-mono-data text-[11px] uppercase tracking-[0.3em]">
            {lang === "fr" ? "Mode Aventurier" : "Adventurer Mode"}
          </span>
        </div>

        <h2 className="font-serif-mystery text-4xl md:text-5xl lg:text-6xl text-zinc-900 mb-6 max-w-4xl leading-[1.05]">
          {T.title}<br />
          <span className="italic text-[#8b6f3d]">{T.titleItalic}</span>
        </h2>
        <p className="text-lg text-zinc-600 max-w-3xl leading-relaxed mb-14">
          {T.intro}
        </p>

        {/* Feature grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mb-16">
          {[
            { icon: <MapPin size={20} />, label: T.f1 },
            { icon: <Compass size={20} />, label: T.f2 },
            { icon: <BookOpen size={20} />, label: T.f3 },
            { icon: <Camera size={20} />, label: T.f4 },
            { icon: <Users size={20} />, label: T.f5 },
          ].map((f, i) => (
            <div
              key={i}
              className="border border-zinc-200 rounded-xl p-5 hover:border-[#8b6f3d]/40 hover:bg-amber-50/30 transition-colors"
              data-testid={`concept-feature-${i}`}
            >
              <div className="w-9 h-9 rounded-lg bg-[#8b6f3d]/10 text-[#8b6f3d] flex items-center justify-center mb-3">
                {f.icon}
              </div>
              <p className="text-sm text-zinc-800 leading-snug">{f.label}</p>
            </div>
          ))}
        </div>

        {/* Badges showcase */}
        <div className="mb-6 flex items-center gap-3 text-[#8b6f3d]">
          <div className="h-px w-8 bg-[#8b6f3d]/40" />
          <span className="font-mono-data text-[10px] uppercase tracking-[0.3em]">{T.badgesLabel}</span>
        </div>
        <h3 className="font-serif-mystery text-3xl md:text-4xl text-zinc-900 mb-3">{T.badgesTitle}</h3>
        <p className="text-zinc-600 max-w-2xl mb-10">{T.badgesIntro}</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {BADGES.map((b, i) => (
            <div
              key={i}
              className="group relative border border-zinc-200 rounded-2xl p-6 hover:border-[#8b6f3d] hover:shadow-lg hover:-translate-y-1 transition-all bg-gradient-to-br from-white to-amber-50/40"
              data-testid={`concept-badge-${i}`}
            >
              <div className="text-5xl mb-3">{b.emoji}</div>
              <div className="font-serif-mystery text-lg text-zinc-900 leading-tight mb-1">
                {lang === "fr" ? b.frName : b.enName}
              </div>
              <p className="text-xs text-zinc-500 font-mono-data leading-relaxed">
                {lang === "fr" ? b.frDesc : b.enDesc}
              </p>
              <div className="absolute top-4 right-4 font-mono-data text-[10px] text-zinc-300">
                #{String(i + 1).padStart(2, "0")}
              </div>
            </div>
          ))}
        </div>

        {/* Verification card */}
        <div className="grid md:grid-cols-2 gap-6 mb-14">
          <div className="border border-zinc-200 rounded-2xl p-6">
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              {T.howItWorks}
            </div>
            <h4 className="font-serif-mystery text-2xl text-[#8b6f3d] mb-3">{T.verifyTitle}</h4>
            <ul className="space-y-2 text-sm text-zinc-700">
              <li className="flex items-start gap-2"><span className="text-[#8b6f3d]">•</span>{T.verify1}</li>
              <li className="flex items-start gap-2"><span className="text-[#8b6f3d]">•</span>{T.verify2}</li>
              <li className="flex items-start gap-2"><span className="text-[#8b6f3d]">•</span>{T.verify3}</li>
            </ul>
          </div>
          <div className="border border-[#8b6f3d]/30 rounded-2xl p-6 bg-gradient-to-br from-amber-50/60 to-white">
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#8b6f3d] mb-2">
              {T.communityLabel}
            </div>
            <h4 className="font-serif-mystery text-2xl text-zinc-900 mb-3">{T.communityTitle}</h4>
            <p className="text-sm text-zinc-700 leading-relaxed mb-4">{T.communityBody}</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/map">
                <Button className="bg-[#8b6f3d] hover:bg-[#a1854b] text-white rounded-full h-11 px-5" data-testid="concept-explore-btn">
                  <Compass size={16} className="mr-2" /> {T.exploreBtn}
                  <ChevronRight size={16} className="ml-1" />
                </Button>
              </Link>
              <Link to="/profile">
                <Button variant="outline" className="border-[#8b6f3d]/50 hover:bg-amber-50 text-[#8b6f3d] rounded-full h-11 px-5" data-testid="concept-profile-btn">
                  {T.myBadgesBtn}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const FR = {
  title: "Devenez un véritable",
  titleItalic: "explorateur des mystères",
  intro: "WorldMysteriesMap n'est pas seulement une carte : c'est une plateforme d'exploration où vous découvrez des lieux mystérieux partout dans le monde, apprenez leur histoire, et participez vous-même aux recherches sur le terrain.",
  f1: "Explorez la carte mondiale des mystères.",
  f2: "Découvrez lieux historiques, paranormaux, archéologiques, militaires et inexpliqués.",
  f3: "Lisez les histoires, consultez les sources et apprenez le contexte historique.",
  f4: "Partez à la découverte réelle et ajoutez photos ou preuves de visite.",
  f5: "Contribuez à enrichir la carte avec vos propres observations.",
  badgesLabel: "Récompenses",
  badgesTitle: "Débloquez vos badges d'explorateur",
  badgesIntro: "Chaque visite réelle validée par GPS débloque des badges. Explorez différentes catégories pour compléter votre collection.",
  howItWorks: "Comment ça marche",
  verifyTitle: "Vérification de présence",
  verify1: "Géolocalisation GPS sur place (rayon de 50 mètres).",
  verify2: "Possibilité d'ajouter une photo ou une preuve de visite.",
  verify3: "Attribution automatique des badges selon les découvertes.",
  communityLabel: "Communauté",
  communityTitle: "Chaque explorateur compte",
  communityBody: "Rejoignez une communauté de curieux qui découvrent, documentent et partagent les mystères du monde. Chaque nouvelle visite enrichit la connaissance collective.",
  exploreBtn: "Explorer la carte",
  myBadgesBtn: "Mes badges",
};

const EN = {
  title: "Become a true",
  titleItalic: "mystery explorer",
  intro: "WorldMysteriesMap isn't just a map — it's an exploration platform where you discover mysterious places worldwide, learn their history, and take part in field research yourself.",
  f1: "Explore the world mystery map.",
  f2: "Discover historical, paranormal, archaeological, military and unexplained places.",
  f3: "Read the stories, check sources and learn the historical context.",
  f4: "Go on real-world discoveries and add photos or proof of visit.",
  f5: "Contribute to the map with your own observations.",
  badgesLabel: "Rewards",
  badgesTitle: "Unlock your explorer badges",
  badgesIntro: "Every real visit validated by GPS unlocks badges. Explore different categories to complete your collection.",
  howItWorks: "How it works",
  verifyTitle: "Presence verification",
  verify1: "On-site GPS geolocation (50-metre radius).",
  verify2: "Add photos or proof of visit.",
  verify3: "Automatic badge attribution based on your discoveries.",
  communityLabel: "Community",
  communityTitle: "Every explorer counts",
  communityBody: "Join a community of curious minds who discover, document and share the world's mysteries. Each new visit enriches the collective knowledge.",
  exploreBtn: "Explore the map",
  myBadgesBtn: "My badges",
};
